"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Scissors, Palette, Layers, Droplet, WandSparkles as Wand2, Heart } from 'lucide-react';
export default function TheExtensionSuite() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const standards = [{
    icon: Scissors,
    title: "Precision Cutting",
    summary: "Tailored silhouettes designed to grow out beautifully and maintain shape.",
    detail: "Our signature precision cuts are crafted to honor your natural hair texture. We utilize dry-cutting techniques to ensure every strand falls perfectly, providing a bespoke look that requires minimal daily styling while maintaining its structural integrity for weeks.",
    badges: ["Dry Cutting", "Bespoke Shape", "Texture Focused"]
  }, {
    icon: Palette,
    title: "Color Artistry",
    summary: "Customized balayage and dimensional color for a lived-in, radiant finish.",
    detail: "We specialize in hand-painted balayage and multi-dimensional color techniques. Our colorists work with premium, low-ammonia formulas to achieve vibrant, healthy results that enhance your natural complexion and provide a seamless, sun-kissed aesthetic.",
    badges: ["Balayage", "Dimensional Color", "Gloss Treatments"]
  }, {
    icon: Layers,
    title: "Extension Mastery",
    summary: "Seamless, damage-free extensions for added volume and length.",
    detail: "Experience the luxury of premium, ethically sourced hair extensions. Our application methods prioritize the health of your natural hair, ensuring a comfortable, undetectable blend that adds instant volume and length with a natural, flowing movement.",
    badges: ["Hand-Tied", "Tape-In", "Seamless Blend"]
  }, {
    icon: Droplet,
    title: "Deep Conditioning",
    summary: "Restorative rituals to nourish, hydrate, and revitalize your hair.",
    detail: "Our salon rituals focus on deep-tissue hair health. Using high-performance botanical treatments, we address dryness, breakage, and scalp health, leaving your hair feeling soft, resilient, and luminous from root to tip.",
    badges: ["Botanical Care", "Scalp Health", "Moisture Lock"]
  }, {
    icon: Wand2,
    title: "Styling Sessions",
    summary: "Professional blowouts and editorial styling for every occasion.",
    detail: "Whether you need a polished, sleek finish or effortless, textured waves, our stylists provide expert blowouts tailored to your hair type. We use professional-grade tools to ensure a long-lasting, high-shine result that feels light and airy.",
    badges: ["Professional Blowout", "Editorial Styling", "Heat Protection"]
  }, {
    icon: Heart,
    title: "Consultation & Care",
    summary: "Personalized hair health plans and maintenance guidance.",
    detail: "Every appointment begins with a thorough consultation to understand your lifestyle and hair goals. We provide a comprehensive care plan, including product recommendations and maintenance schedules to keep your hair looking salon-fresh between visits.",
    badges: ["Personalized Plan", "Home Care", "Expert Advice"]
  }];
  return <section data-section-id="4427" id="the-extension-suite" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-52">
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-lg opacity-30" style={{
      background: "radial-gradient(circle, hsl(340 50% 45% / 0.15) 0%, transparent 70%)",
      filter: "blur(60px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="font-default text-xs tracking-[0.3em] uppercase mb-8" style={{
              color: "var(--primary)"
            }}>
                Our Expertise
              </p>
              <h2 className="font-default font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground mb-8">
                Artistry in every strand.
              </h2>
              <p className="text-base md:text-lg leading-relaxed font-light max-w-md" style={{
              color: "var(--muted-foreground)"
            }}>
                At Velvet Shears, we believe hair is the ultimate accessory. Our curated services are designed to elevate your natural beauty through precision, creativity, and a commitment to hair health that lasts long after you leave our chair.
              </p>

              <div className="mt-10 hidden lg:block">
                <div className="h-px w-16 mb-6" style={{
                backgroundColor: "var(--primary)"
              }} />
                <p className="font-default text-xs tracking-widest uppercase text-muted-foreground">
                  Six Core Services
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div ref={listRef} className={`transition-all duration-1000 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Accordion type="single" collapsible defaultValue="item-0" className="w-full space-y-6">
                {standards.map((standard, i) => {
                const Icon = standard.icon;
                return <AccordionItem key={i} value={`item-${i}`} data-index={i} className="group relative rounded-lg border border-border bg-card/40 backdrop-blur-sm transition-all duration-500 hover:bg-card/70 data-[state=open]:bg-card/80 overflow-hidden last:border-b">
                      <span aria-hidden="true" className="absolute left-0 top-0 h-full w-px opacity-0 transition-opacity duration-500 group-data-[state=open]:opacity-100" style={{
                    backgroundColor: "var(--primary)"
                  }} />

                      <AccordionTrigger className="px-6 md:px-8 py-6 md:py-7 hover:no-underline [&>svg]:text-foreground/60 [&>svg]:size-5">
                        <div className="flex items-start gap-5 md:gap-6 text-left flex-1">
                          <Icon className="h-6 w-6 mt-1 shrink-0 stroke-[1.25] text-primary" strokeWidth={1.25} />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-default font-light text-2xl md:text-[26px] leading-tight text-foreground mb-2">
                              {standard.title}
                            </h3>
                            <p className="text-sm md:text-base font-light leading-relaxed" style={{
                          color: "var(--muted-foreground)"
                        }}>
                              {standard.summary}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-6 md:px-8 pb-8">
                        <div className="pl-0 md:pl-12">
                          <div className="h-px w-12 mb-6" style={{
                        backgroundColor: "var(--primary)"
                      }} />
                          <p className="text-base font-light leading-[1.75] text-foreground/85 mb-6">
                            {standard.detail}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {standard.badges.map((badge, j) => <Badge key={j} data-index={j} variant="outline" className="rounded-lg px-3 py-1 font-default text-[10px] tracking-widest uppercase border-transparent" style={{
                          backgroundColor: "var(--accent)",
                          color: "var(--primary)"
                        }}>
                                {badge}
                              </Badge>)}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>;
              })}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}